import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import {
  caracteresRestantes,
  folgaDoTitulo,
  wireseoApi,
  type VeredictoRegua,
} from '@/services/wireseo';

import styles from './ReguaManchete.module.css';

interface Props {
  titulo: string;
  site: string | null | undefined;
  entidade?: string | null;
  larguraImagem?: number | null;
}

/**
 * Régua de manchete ao vivo, abaixo do campo Título.
 *
 * Mostra o título SERVIDO — com o sufixo que o tema do site cola antes de
 * mandar ao Google. É a única forma de o defeito ficar visível: no ncnews o
 * sufixo soma 10 caracteres e 18 de 20 matérias de uma amostra passavam do teto
 * de 70 por causa dele, enquanto na tela o título parecia curto.
 *
 * Não bloqueia nada. Avisa.
 */
export function ReguaManchete({ titulo, site, entidade, larguraImagem }: Props) {
  const { t } = useTranslation(['common', 'nav', 'board', 'articles', 'analytics', 'feeddrive', 'config', 'strategist']);
  void t;
  // Espera o operador parar de digitar: a régua é conferência, não autocomplete.
  const [debounced, setDebounced] = useState(titulo);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(titulo), 400);
    return () => clearTimeout(t);
  }, [titulo]);

  const q = useQuery({
    queryKey: ['wireseo', 'regua', site, debounced, entidade, larguraImagem],
    queryFn: () =>
      wireseoApi.conferir({
        titulo: debounced,
        site,
        entidade,
        largura_imagem: larguraImagem,
      }),
    enabled: Boolean(site) && debounced.trim().length > 0,
    retry: false,
    staleTime: 30_000,
  });

  const d: VeredictoRegua | undefined = q.data;
  if (!d) return null;

  const m = d.manchete;
  const folga = folgaDoTitulo(m);
  const restam = caracteresRestantes(m);
  const pct = Math.min(100, Math.round((m.chars / m.max) * 100));
  const semSufixo = m.chars - d.sufixo_do_tema.length;

  return (
    <div className={styles.root} data-testid="regua-manchete">
      <div className={styles.head}>
        <span className={styles.label}>
          Como o Google recebe {d.sufixo_do_tema ? '(com o sufixo do tema)' : ''}
        </span>
        <span className={`${styles.contador} ${styles[folga]}`}>
          {m.chars}/{m.max}
          {restam < 0 ? ` · passou ${Math.abs(restam)}` : ` · cabem ${restam}`}
        </span>
      </div>

      <div className={styles.barra}>
        <div
          className={`${styles.preenchimento} ${styles[folga]}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {d.sufixo_do_tema && (
        <p className={styles.servido}>
          {m.servido.slice(0, semSufixo)}
          <span className={styles.sufixo}>{d.sufixo_do_tema}</span>
        </p>
      )}

      {m.problemas.length > 0 && (
        <ul className={styles.problemas}>
          {m.problemas.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}

      {'largura' in d.capa && !d.capa.elegivel_discover && (
        <p className={styles.problemas}>
          Capa com {d.capa.largura}px — abaixo dos {d.capa.minimo}px que o
          Discover exige para mostrar imagem grande. Precisa de foto melhor;
          ampliar só borraria.
        </p>
      )}

      {d.sufixo_do_tema && (
        <p className={styles.nota}>
          O tema soma <code>{d.sufixo_do_tema}</code> a toda matéria. Seu espaço
          real é de {m.orcamento} caracteres, não {m.max}.
        </p>
      )}
    </div>
  );
}
